<template>
  <st-modal size="medium" :on-close="close">
    <div class="st-bo-change-billing-address-modal">
      <div class="st-bo-change-billing-address-modal__title">
        <StHeadline :level="2">Byt fakturaadress</StHeadline>
      </div>
      <div class="st-bo-change-billing-address-modal__body">
        <span class="st-bo-change-billing-address-modal__body__span">
          Välj den fakturaadress du vill byta till
        </span>
        <div class="st-bo-change-billing-address-modal__body__select">
          <st-select
            :items="options"
            @change="(value) => handleSelect(value)"
            :model-value="selectedOption"
            :hasEmpty="false"
          />
        </div>
        <st-checkbox
          v-model="applyForAddressChecked"
          class="st-bo-change-billing-address-modal__body__checkbox"
          @click="handleChecked"
          tabindex="0"
          aria-label="Ansök om en annan fakturaadress"
          icon-description=""
        >
          Ansök om en annan fakturaadress
        </st-checkbox>
        <div
          v-if="applyForAddressChecked"
          class="st-bo-change-billing-address-modal__body__apply-inputs"
        >
          <st-form-group
            label="Adress"
            required
            :validation="v$.street"
            :validation-errors="validationErrors.street"
          >
            <st-input
              id="account-billing-information-input-address"
              v-model="applyAddress.street"
              tabindex="0"
              :required="true"
              :invalid="v$.street.$error"
              :blur="() => v$.street.$touch()"
            />
          </st-form-group>
          <st-form-group
            label="C/O"
            :validation="v$.co"
            :validation-errors="validationErrors.co"
          >
            <st-input
              id="account-billing-information-input-co"
              v-model="applyAddress.co"
              tabindex="0"
              :invalid="false"
              :required="false"
              :blur="() => v$.co.$touch()"
            />
          </st-form-group>
          <st-form-group
            label="Postnummer"
            required
            :validation="v$.postalCode"
            :validation-errors="validationErrors.postalCode"
          >
            <st-input
              id="account-billing-information-input-post"
              v-model="applyAddress.postalCode"
              tabindex="0"
              :required="false"
              :invalid="v$.postalCode.$error"
              :blur="() => v$.postalCode.$touch()"
            />
          </st-form-group>
          <st-form-group
            label="Ort"
            required
            :validation="v$.city"
            :validation-errors="validationErrors.city"
          >
            <st-input
              id="account-billing-information-input-city"
              v-model="applyAddress.city"
              tabindex="0"
              :required="false"
              :invalid="v$.city.$error"
              :blur="() => v$.city.$touch()"
            />
          </st-form-group>
        </div>
        <div class="st-bo-change-billing-address-modal__body__buttons">
          <st-button
            rectangular
            fullWidth
            tabindex="0"
            @click="changeAddress"
            :disabled="!validAddress()"
            :is-loading="isLoading"
          >
            {{
              applyForAddressChecked
                ? "Ansök om ny fakturaadress"
                : "Byt fakturaadress"
            }}
          </st-button>
          <st-button
            rectangular
            secondary
            full-width
            @click="close()"
            tabindex="0"
          >
            Avbryt
          </st-button>
        </div>
      </div>
    </div>
  </st-modal>
</template>

<script>
import {
  StModal,
  StButton,
  StInput,
  StCheckbox,
  StFormGroup,
  StSelect,
  StHeadline,
} from "skanetrafiken";
import { useAccount } from "@/composables/useAccount";
import { onUpdated, ref, computed, onMounted } from "vue";
import ChangeBillingAddressValidations from "@/validators/change-billing-address-validations";
import ChangeBillingAddressValidationsErrors from "@/validators/change-billing-address-validations-errors";
import { useVuelidate } from "@vuelidate/core";
import { CLOSE } from "@/constants";
export default {
  name: "st-bo-change-billing-address-modal",
  components: {
    StModal,
    StButton,
    StInput,
    StCheckbox,
    StSelect,
    StFormGroup,
    StHeadline,
  },
  props: {
    isLoading: Boolean,
  },
  setup(_, { emit }) {
    const account = useAccount();
    const applyForAddressChecked = ref(false);
    const applyAddress = ref({
      id: "",
      street: "",
      co: "",
      postalCode: "",
      city: "",
      sveaAddressSelector: null,
      companyName: account.state.account.organizationName,
    });
    const v$ = useVuelidate(ChangeBillingAddressValidations, applyAddress, {
      $scope: false,
    });
    const addresses = ref([]);
    const selectedOption = ref(0);

    onMounted(() => {
      addresses.value = [];
      addresses.value = account.state.account.authorizedAddresses.map(
        (address, index) => {
          return {
            name: `${address.co || ""}${address.co ? "," : ""} ${
              address.street
            }, ${address.postalCode} ${address.city}`,
            id: index + 1,
          };
        }
      );
      addresses.value.unshift({ name: "", id: 0 });
      selectedOption.value = addresses.value[0].id;
    });

    onUpdated(() => {
      applyForAddressChecked.value = false;
      resetAddress();
      selectedOption.value = addresses.value[0].id;
    });

    const handleSelect = (e) => {
      const index = e.target.value;
      selectedOption.value = e.target.value;
      if (index == 0) {
        resetAddress();
      } else {
        Object.assign(
          applyAddress.value,
          account.state.account.authorizedAddresses[index - 1]
        );
      }
      applyForAddressChecked.value = false;
    };

    const handleChecked = () => {
      if (!applyForAddressChecked.value) {
        resetAddress();
        selectedOption.value = addresses.value[0].id;
      }
      applyForAddressChecked.value = !applyForAddressChecked.value;
    };

    const validAddress = () => {
      return (
        applyAddress.value.street.length > 0 &&
        applyAddress.value.postalCode.length > 0 &&
        applyAddress.value.city.length > 0
      );
    };

    const resetAddress = () => {
      applyAddress.value = {
        id: "",
        street: "",
        co: "",
        postalCode: "",
        city: "",
        sveaAddressSelector: null,
        companyName: account.state.account.organizationName,
      };
    };

    const handleBlur = (prop) => {
      v$.value[prop].$touch();
    };

    const changeAddress = () => {
      v$.value.$touch();
      const errors = v$.value.$errors;
      if (errors.length) return;
      emit("updateAddress", applyAddress.value, applyForAddressChecked.value);
    };

    const close = () => {
      v$.value.$reset();
      emit(CLOSE);
    };

    return {
      account: computed(() => account.state.account),
      applyForAddressChecked,
      options: computed(() => addresses.value),
      applyAddress,
      handleSelect,
      validAddress,
      handleChecked,
      selectedOption,
      v$,
      handleBlur,
      validationErrors: ChangeBillingAddressValidationsErrors,
      changeAddress,
      close,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "~skanetrafiken/dist/scss/main.scss";
.st-bo-change-billing-address-modal {
  text-align: left;

  &__title {
    text-align: center;
    margin-bottom: rem-calc(16);
  }

  &__body {
    & > :not(:last-child) {
      margin-bottom: rem-calc(16);
    }

    &__span {
      font-family: $source-sans-pro;
      color: $text-color-primary;
    }

    &__select {
      margin-top: rem-calc(10);
    }

    &__apply-inputs {
      & > :not(:last-child) {
        margin-bottom: rem-calc(16);
      }
    }

    &__checkbox {
      margin-top: 0;
    }

    &__buttons {
      display: flex;
      flex-direction: column;

      & > :last-child {
        margin-top: rem-calc(10);
      }
    }
  }
}
</style>
